import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Search, Keyboard, Plus, Trash2 } from 'lucide-react';
import { useKeyboardContext, eventToKeyCombo, serializeKeyCombo, KeyCombo } from '../../contexts/KeyboardContext';
import { toolsRegistry } from '../ToolsMenu/config/tools.registry';
import { useNotifications } from '../feedback/NotificationProvider';
import { useUserShortcuts } from '../../hooks/useUserShortcuts';

type Props = {
  onClose: () => void;
};

interface ToolBinding {
  toolId: string;
  toolName: string;
  toolIcon: React.ComponentType<any>;
  actionId: string;
  actionLabel: string;
  keys: KeyCombo;
  serialized: string;
}

export function ShortcutConfig({ onClose }: Props) {
  const { listBindings, setShortcut, removeShortcut, hydrate } = useKeyboardContext();
  const { notify } = useNotifications();
  const { updateShortcut: saveShortcutToBackend, removeShortcut: removeShortcutFromBackend, loadShortcuts } = useUserShortcuts();
  const [toolId, setToolId] = useState<string>('oneregua');
  const [actionId, setActionId] = useState<string>('abrir-modal');
  const [capturing, setCapturing] = useState<boolean>(false);
  const [lastCaptured, setLastCaptured] = useState<string>('');
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [filterMode, setFilterMode] = useState<'all' | 'with-shortcuts' | 'without-shortcuts'>('all');

  const captureRef = useRef<HTMLDivElement | null>(null);
  const currentBindingRef = useRef<typeof bindings[0] | undefined>(undefined);

  const bindings = useMemo(() => listBindings(), [listBindings]);

  const enabledTools = useMemo(() => 
    toolsRegistry
      .filter(t => !t.disabled)
      .sort((a, b) => a.name.localeCompare(b.name, 'pt-BR'))
  , []);

  const toolActionsMap = useMemo<Record<string, { id: string; label: string }[]>>(() => {
    const map: Record<string, { id: string; label: string }[]> = {};
    for (const def of toolsRegistry) {
      const actions: { id: string; label: string }[] = [];
      // Se a ferramenta tem uma action principal, oferecemos esta action
      if ((def as any).actionId) {
        const a = String((def as any).actionId);
        const actionLabel = a === 'command:1' ? 'Executar comando 1' :
                          a === 'command:2' ? 'Executar comando 2' :
                          a === 'command:3' ? 'Executar comando 3' :
                          a === 'command:4' ? 'OneContorno' :
                          a === 'smartimport' ? 'Importação inteligente' :
                          a === 'runps' ? 'Executar script PS' : a;
        actions.push({ id: a, label: actionLabel });
      }
      // Se a ferramenta abre modal, seguimos o padrão existente "abrir-modal"
      if ((def as any).opensModalId) {
        actions.push({ id: 'abrir-modal', label: 'Abrir modal' });
      }
      map[def.id] = actions;
    }
    return map;
  }, []);

  // Agrupar bindings por ferramenta com informações completas
  const groupedBindings = useMemo(() => {
    const groups: Record<string, ToolBinding[]> = {};
    
    // Inicializar grupos para todas as ferramentas habilitadas
    enabledTools.forEach(tool => {
      groups[tool.id] = [];
    });

    // Preencher com bindings existentes
    bindings.forEach(binding => {
      const tool = toolsRegistry.find(t => t.id === binding.toolId);
      if (tool) {
        const action = toolActionsMap[binding.toolId]?.find(a => a.id === binding.actionId);
        if (!groups[binding.toolId]) {
          groups[binding.toolId] = [];
        }
        groups[binding.toolId].push({
          toolId: binding.toolId,
          toolName: tool.name,
          toolIcon: tool.icon,
          actionId: binding.actionId,
          actionLabel: action?.label || binding.actionId,
          keys: binding.keys,
          serialized: serializeKeyCombo(binding.keys)
        });
      }
    });

    return groups;
  }, [bindings, enabledTools, toolActionsMap]);

  // Filtrar e buscar ferramentas
  const filteredTools = useMemo(() => {
    let tools = enabledTools;
    
    // Aplicar busca
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      tools = tools.filter(t => 
        t.name.toLowerCase().includes(query) || 
        t.id.toLowerCase().includes(query)
      );
    }
    
    // Aplicar filtro
    if (filterMode === 'with-shortcuts') {
      tools = tools.filter(t => groupedBindings[t.id]?.length > 0);
    } else if (filterMode === 'without-shortcuts') {
      tools = tools.filter(t => !groupedBindings[t.id] || groupedBindings[t.id].length === 0);
    }
    
    return tools;
  }, [enabledTools, searchQuery, filterMode, groupedBindings]);

  // Estatísticas
  const stats = useMemo(() => {
    const totalTools = enabledTools.length;
    const toolsWithShortcuts = Object.values(groupedBindings).filter(b => b.length > 0).length;
    const totalShortcuts = bindings.length;
    
    return {
      totalTools,
      toolsWithShortcuts,
      totalShortcuts,
      percentage: totalTools > 0 ? Math.round((toolsWithShortcuts / totalTools) * 100) : 0
    };
  }, [enabledTools.length, groupedBindings, bindings.length]);

  // Encontrar atalho atual para a ferramenta/ação selecionada
  const currentBinding = useMemo(() => {
    return bindings.find(b => b.toolId === toolId && b.actionId === actionId);
  }, [bindings, toolId, actionId]);

  useEffect(() => {
    const list = toolActionsMap[toolId];
    if (list && list.length > 0) {
      setActionId(list[0].id);
    }
    // Limpar lastCaptured ao mudar ferramenta
    setLastCaptured('');
  }, [toolId, toolActionsMap]);

  useEffect(() => {
    // Atualizar ref e lastCaptured quando há um binding atual
    currentBindingRef.current = currentBinding;
    if (currentBinding) {
      setLastCaptured(serializeKeyCombo(currentBinding.keys));
    } else {
      setLastCaptured('');
    }
  }, [currentBinding]);

  const startCapture = useCallback(() => {
    setCapturing(true);
    setLastCaptured('');
    setTimeout(() => captureRef.current?.focus(), 0);
  }, []);

  const onKeyDownCapture = useCallback(async (e: React.KeyboardEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    const combo = eventToKeyCombo(e.nativeEvent as unknown as KeyboardEvent);
    
    // Se for Escape, apenas cancelar
    if (e.key === 'Escape') {
      setCapturing(false);
      // Restaurar último valor válido ou limpar (usar ref para evitar closure stale)
      const binding = currentBindingRef.current;
      if (binding) {
        setLastCaptured(serializeKeyCombo(binding.keys));
      } else {
        setLastCaptured('');
      }
      return;
    }
    
    // Verificar se tem pelo menos uma tecla
    if (combo.length === 0) return;
    
    // Identificar modificadores e tecla principal
    const modifiers = ['Control', 'Shift', 'Alt', 'Meta'];
    const mainKey = combo.find(k => !modifiers.includes(k));
    
    // Se ainda não tem tecla principal (só modificadores), aguardar
    if (!mainKey) return;
    
    // Backend aceita qualquer tecla, com ou sem modificador
    const serialized = serializeKeyCombo(combo);
    setLastCaptured(serialized || '');
    
    // 1. Atualizar no contexto local (para feedback imediato)
    const result = setShortcut({ toolId, actionId, keys: combo });
    if (result.conflict) {
      const existingTool = toolsRegistry.find(t => t.id === result.conflict!.existing.toolId);
      const existingAction = toolActionsMap[result.conflict!.existing.toolId]?.find(
        a => a.id === result.conflict!.existing.actionId
      );
      const existingName = existingTool?.name || result.conflict!.existing.toolId;
      const existingActionLabel = existingAction?.label || result.conflict!.existing.actionId;
      
      notify({
        type: 'warning',
        title: 'Conflito de atalho',
        message: `Este atalho já está em uso por "${existingName}" → "${existingActionLabel}". Será sobrescrito.`,
        duration: 5000
      });
    }
    
    // 2. Salvar no backend (API)
    const success = await saveShortcutToBackend(toolId, actionId, combo);
    if (success) {
      const currentTool = toolsRegistry.find(t => t.id === toolId);
      notify({
        type: 'success',
        title: 'Atalho configurado',
        message: `Atalho "${serialized}" definido para ${currentTool?.name || toolId}`,
        duration: 3000
      });
      
      // 3. Recarregar atalhos do backend e hidratar o contexto
      const updated = await loadShortcuts();
      if (updated) {
        hydrate(updated);
      }
    } else {
      notify({
        type: 'error',
        title: 'Erro ao salvar',
        message: 'Não foi possível salvar o atalho no servidor. Tente novamente.',
        duration: 5000
      });
    }
    
    setCapturing(false);
  }, [toolId, actionId, setShortcut, toolActionsMap, notify, saveShortcutToBackend, loadShortcuts, hydrate]);

  const handleRemove = useCallback(async (toolIdToRemove: string, actionIdToRemove: string) => {
    const tool = toolsRegistry.find(t => t.id === toolIdToRemove);
    
    // 1. Remover do contexto local
    removeShortcut(toolIdToRemove, actionIdToRemove);
    
    // 2. Remover do backend (API)
    const success = await removeShortcutFromBackend(toolIdToRemove, actionIdToRemove);
    if (success) {
      notify({
        type: 'info',
        title: 'Atalho removido',
        message: `Atalho de "${tool?.name || toolIdToRemove}" foi removido. Voltará a usar o padrão.`,
        duration: 3000
      });
      
      // 3. Recarregar atalhos do backend e hidratar o contexto
      const updated = await loadShortcuts();
      if (updated) {
        hydrate(updated);
      }
    } else {
      notify({
        type: 'error',
        title: 'Erro ao remover',
        message: 'Não foi possível remover o atalho no servidor. Tente novamente.',
        duration: 5000
      });
    }
  }, [removeShortcut, removeShortcutFromBackend, notify, loadShortcuts, hydrate]);

  // ESC para fechar modal quando não está capturando
  useEffect(() => {
    if (capturing) return;
    const handleEsc = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !capturing) {
        onClose();
      }
    };
    document.addEventListener('keydown', handleEsc);
    return () => document.removeEventListener('keydown', handleEsc);
  }, [capturing, onClose]);

  // ESC para cancelar captura
  useEffect(() => {
    if (!capturing) return;
    const esc = (e: KeyboardEvent) => { 
      if (e.key === 'Escape') {
        setCapturing(false);
        // Restaurar último valor válido (usar ref para evitar closure stale)
        const binding = currentBindingRef.current;
        if (binding) {
          setLastCaptured(serializeKeyCombo(binding.keys));
        } else {
          setLastCaptured('');
        }
      }
    };
    document.addEventListener('keydown', esc);
    return () => document.removeEventListener('keydown', esc);
  }, [capturing]);

  const selectedTool = toolsRegistry.find(t => t.id === toolId);
  const selectedAction = toolActionsMap[toolId]?.find(a => a.id === actionId);

  return (
    <div 
      className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
      onClick={(e) => {
        // Fechar ao clicar no overlay (mas não no conteúdo da modal)
        if (e.target === e.currentTarget) {
          onClose();
        }
      }}
    >
      <div 
        className="bg-navy-800 border border-navy-700 rounded-lg shadow-xl w-full max-w-4xl max-h-[90vh] overflow-hidden flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-navy-700">
          <div className="flex items-center gap-3">
            <Keyboard className="w-5 h-5 text-blue-400" />
            <h3 className="text-lg font-semibold text-gray-100">Gerenciar Atalhos de Teclado</h3>
          </div>
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm rounded bg-blue-600 hover:bg-blue-500 text-white transition-colors flex items-center gap-2"
          >
            Salvar e Fechar
          </button>
        </div>

        <div className="flex-1 overflow-auto p-4">
          {/* Seção: Criar Novo Atalho */}
          <div className="mb-6">
            <div className="flex items-center gap-2 mb-3">
              <Plus className="w-4 h-4 text-blue-400" />
              <h4 className="text-sm font-semibold text-gray-200">Criar Novo Atalho</h4>
            </div>
            <div className="bg-navy-900 border border-navy-700 rounded-lg p-4 space-y-3">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                <div>
                  <label className="block text-xs text-gray-400 mb-1.5">Ferramenta</label>
                  <select
                    value={toolId}
                    onChange={(e) => setToolId(e.target.value)}
                    className="w-full px-3 py-2 text-sm bg-navy-800 border border-navy-700 rounded text-gray-200 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <optgroup label="Ferramentas Ativas">
                      {enabledTools.map(t => (
                        <option key={t.id} value={t.id}>{t.name}</option>
                      ))}
                    </optgroup>
                  </select>
                </div>
                <div>
                  <label className="block text-xs text-gray-400 mb-1.5">Ação</label>
                  {(!toolActionsMap[toolId] || toolActionsMap[toolId].length === 0) ? (
                    <div className="w-full px-3 py-2 text-sm bg-navy-800/50 border border-navy-700/50 rounded text-gray-500 italic">
                      Nenhuma ação disponível
                    </div>
                  ) : (
                    <select
                      value={actionId}
                      onChange={(e) => {
                        setActionId(e.target.value);
                        setLastCaptured(''); // Limpar ao mudar ação
                      }}
                      className="w-full px-3 py-2 text-sm bg-navy-800 border border-navy-700 rounded text-gray-200 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                      {(toolActionsMap[toolId] || []).map(opt => (
                        <option key={opt.id} value={opt.id}>{opt.label}</option>
                      ))}
                    </select>
                  )}
                </div>
                <div>
                  <label className="block text-xs text-gray-400 mb-1.5">Combinação de Teclas</label>
                  {!capturing ? (
                    <div className="flex items-center gap-2">
                      <div className="flex-1 px-3 py-2 text-sm bg-navy-800 border border-navy-700 rounded text-gray-300 font-mono">
                        {lastCaptured || '—'}
                      </div>
                      <button
                        onClick={startCapture}
                        className="px-4 py-2 text-sm rounded bg-blue-600 hover:bg-blue-500 text-white transition-colors whitespace-nowrap"
                      >
                        Definir…
                      </button>
                    </div>
                  ) : (
                    <div
                      ref={captureRef}
                      tabIndex={0}
                      onKeyDown={onKeyDownCapture}
                      className="px-3 py-2 text-sm bg-blue-900/30 border-2 border-blue-500 rounded text-blue-300 font-mono outline-none animate-pulse"
                    >
                      Pressione a combinação... (Esc para cancelar)
                    </div>
                  )}
                </div>
              </div>
              {selectedTool && selectedAction && (
                <div className="flex items-center justify-between text-xs">
                  <div className="text-gray-400 flex items-center gap-2">
                    <span>Configurando:</span>
                    <span className="text-blue-300 font-medium">
                      {selectedTool.name} → {selectedAction.label}
                    </span>
                  </div>
                  {currentBinding && (
                    <div className="text-gray-500 flex items-center gap-2">
                      <span>Atalho atual:</span>
                      <span className="text-gray-300 font-mono">
                        {serializeKeyCombo(currentBinding.keys)}
                      </span>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* Seção: Busca e Filtros */}
          <div className="mb-6">
            <div className="flex flex-col sm:flex-row gap-3">
              <div className="flex-1 relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                <input
                  type="text"
                  placeholder="Buscar ferramenta..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 text-sm bg-navy-900 border border-navy-700 rounded text-gray-200 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div className="flex gap-2">
                <button
                  onClick={() => setFilterMode('all')}
                  className={`px-3 py-2 text-xs rounded border transition-colors ${
                    filterMode === 'all'
                      ? 'bg-blue-600 border-blue-500 text-white'
                      : 'bg-navy-900 border-navy-700 text-gray-300 hover:bg-navy-800'
                  }`}
                >
                  Todas
                </button>
                <button
                  onClick={() => setFilterMode('with-shortcuts')}
                  className={`px-3 py-2 text-xs rounded border transition-colors ${
                    filterMode === 'with-shortcuts'
                      ? 'bg-blue-600 border-blue-500 text-white'
                      : 'bg-navy-900 border-navy-700 text-gray-300 hover:bg-navy-800'
                  }`}
                >
                  Com atalhos
                </button>
                <button
                  onClick={() => setFilterMode('without-shortcuts')}
                  className={`px-3 py-2 text-xs rounded border transition-colors ${
                    filterMode === 'without-shortcuts'
                      ? 'bg-blue-600 border-blue-500 text-white'
                      : 'bg-navy-900 border-navy-700 text-gray-300 hover:bg-navy-800'
                  }`}
                >
                  Sem atalhos
                </button>
              </div>
            </div>
          </div>

          {/* Estatísticas */}
          <div className="mb-4 flex items-center gap-4 text-sm">
            <div className="text-gray-400">
              <span className="text-gray-200 font-semibold">{stats.totalShortcuts}</span> atalhos configurados
            </div>
            <div className="text-gray-400">
              <span className="text-gray-200 font-semibold">{stats.toolsWithShortcuts}</span> de{' '}
              <span className="text-gray-200 font-semibold">{stats.totalTools}</span> ferramentas ({stats.percentage}%)
            </div>
          </div>

          {/* Seção: Atalhos por Ferramenta */}
          <div className="space-y-3">
            {filteredTools.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                <Search className="w-8 h-8 mx-auto mb-2 opacity-50" />
                <p>Nenhuma ferramenta encontrada</p>
              </div>
            ) : (
              filteredTools.map(tool => {
                const toolBindings = groupedBindings[tool.id] || [];
                const ToolIcon = tool.icon;
                
                return (
                  <div
                    key={tool.id}
                    className="bg-navy-900 border border-navy-700 rounded-lg overflow-hidden"
                  >
                    {/* Header da Ferramenta */}
                    <div className="px-4 py-3 bg-navy-800/50 border-b border-navy-700 flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <ToolIcon className="w-5 h-5 text-blue-400" />
                        <div>
                          <h5 className="text-sm font-semibold text-gray-200">{tool.name}</h5>
                          <p className="text-xs text-gray-500">{tool.id}</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        {toolBindings.length > 0 ? (
                          <span className="px-2 py-1 text-xs rounded bg-blue-600/20 text-blue-300 border border-blue-600/30">
                            {toolBindings.length} {toolBindings.length === 1 ? 'atalho' : 'atalhos'}
                          </span>
                        ) : (
                          <span className="px-2 py-1 text-xs rounded bg-gray-700/50 text-gray-500 border border-gray-700">
                            Sem atalhos
                          </span>
                        )}
                      </div>
                    </div>

                    {/* Lista de Atalhos */}
                    <div className="p-4 space-y-2">
                      {toolBindings.length === 0 ? (
                        <div className="text-sm text-gray-500 italic py-2">
                          Nenhum atalho configurado para esta ferramenta
                        </div>
                      ) : (
                        toolBindings.map((binding, idx) => (
                          <div
                            key={`${binding.toolId}::${binding.actionId}::${idx}`}
                            className="flex items-center justify-between p-3 bg-navy-800/50 rounded border border-navy-700/50 hover:border-navy-600 transition-colors"
                          >
                            <div className="flex-1 min-w-0">
                              <div className="text-sm text-gray-200 font-medium mb-1">
                                {binding.actionLabel}
                              </div>
                              <div className="text-xs text-gray-500 font-mono">
                                {binding.actionId}
                              </div>
                            </div>
                            <div className="flex items-center gap-3">
                              <div className="px-3 py-1.5 bg-navy-700 rounded border border-navy-600 text-sm font-mono text-gray-200">
                                {binding.serialized}
                              </div>
                              <button
                                onClick={() => handleRemove(binding.toolId, binding.actionId)}
                                className="p-2 rounded hover:bg-red-600/20 text-gray-400 hover:text-red-400 transition-colors"
                                title="Remover atalho"
                              >
                                <Trash2 className="w-4 h-4" />
                              </button>
                            </div>
                          </div>
                        ))
                      )}
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </div>
      </div>
    </div>
  );
}


